import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useMemo } from 'react';
import {
    R2FThresholdData,
    PendingThresholdChange,
    R2FProductionCapabilityData,
    R2FNodeData
} from 'src/features/types/r2fTypes';
import { WarehouseHandlingTier } from 'src/types/WarehouseHandlingTier';
import {
    getR2FThresholds,
    publishR2FThresholds,
    deleteR2FThresholds
} from 'src/utils/Queries';
import {
    GetR2FThresholdsResponse,
    HandlingTierByPC,
    PublishedR2FThresholds,
    R2FToPublish,
    DeleteR2FThresholdsRequest,
    WarehouseR2FData
} from 'src/features/types/gslaApi';

/**
 * Custom hook for managing R2F threshold data
 */
export function useR2FThresholds(
    warehouseId: string, 
    userAlias: string = '', 
    userCanEdit: boolean = false,
    preserveDefaultTypes: boolean = true
) {
    const queryClient = useQueryClient();
    
    // Ensure warehouseId is never empty
    const effectiveWarehouseId = warehouseId || 'DEFAULT';
    
    // Fetch threshold data - now using getR2FThresholds which includes DEFAULT warehouse data
    const { data: apiData, isLoading, error } = useQuery(
        ['r2f-thresholds', effectiveWarehouseId, preserveDefaultTypes],
        async () => {
            // getR2FThresholds returns the parsed JSON data directly (not a Response object)
            const responseData = await getR2FThresholds([effectiveWarehouseId]) as GetR2FThresholdsResponse;
            if (!responseData) return undefined;
            
            // Extract data for the specific warehouse
            const specificWarehouseData = responseData.warehouseR2Fs?.[effectiveWarehouseId];
            
            // Extract data for the DEFAULT warehouse (contains Default_Network_PC)
            const defaultWarehouseData = responseData.warehouseR2Fs?.['DEFAULT'];
            
            // Merge thresholds from both warehouses
            let mergedThresholds: HandlingTierByPC = {};
            
            // Start with DEFAULT warehouse thresholds (if they exist)
            if (defaultWarehouseData?.thresholds) {
                mergedThresholds = { ...defaultWarehouseData.thresholds };
            }
            
            // Override with specific warehouse thresholds (if they exist)
            if (specificWarehouseData?.thresholds) {
                // Merge each production capability, combining thresholds from both sources
                Object.entries(specificWarehouseData.thresholds).forEach(([pcName, pcThresholds]) => {
                    if (mergedThresholds[pcName]) {
                        // Merge thresholds for this PC, with specific warehouse taking precedence
                        mergedThresholds[pcName] = {
                            ...mergedThresholds[pcName],
                            ...pcThresholds
                        };
                    } else {
                        // Add new PC from specific warehouse
                        mergedThresholds[pcName] = pcThresholds;
                    }
                });
            }
            
            // If no data from either warehouse, return undefined
            if (Object.keys(mergedThresholds).length === 0) {
                return undefined;
            }
            
            // Convert to the format expected by adaptApiResponse
            const result = {
                warehouseId: effectiveWarehouseId,
                r2fThreshold: mergedThresholds
            };
            
            return result;
        },
        {
            enabled: true,
            staleTime: 5 * 60 * 1000, // 5 minutes instead of Infinity
            cacheTime: 10 * 60 * 1000, // 10 minutes
            refetchOnWindowFocus: false
        }
    );

    // Adapt API response to component format
    const adaptedData = useMemo<R2FThresholdData | undefined>(() => {
        if (!apiData) return undefined;
        return adaptApiResponse(apiData, effectiveWarehouseId);
    }, [apiData, effectiveWarehouseId]);

    // Mutation for saving thresholds
    const saveMutation = useMutation<void, Error, PendingThresholdChange[]>(
        async (changes: PendingThresholdChange[]) => {
            // Convert changes to API request format
            const r2fToPublishList = prepareThresholdPublishRequest(changes, effectiveWarehouseId, userAlias);
            
            // Call API
            const result = await publishR2FThresholds({ r2fToPublishList });
            if (!result) {
                throw new Error('Failed to publish R2F thresholds');
            }
            return;
        },
        {
            onSuccess: () => {
                // Invalidate queries to refresh data
                queryClient.invalidateQueries(['r2f-thresholds', effectiveWarehouseId]);
            }
        }
    );

    // Save function to pass to the component
    const saveThresholds = async (changes: PendingThresholdChange[]) => {
        // Extract deletion changes to handle separately
        const deletionChanges = changes.filter(change => change.action === 'Delete');
        const nonDeletionChanges = changes.filter(change => change.action !== 'Delete');
        
        // Process deletion changes first
        for (const change of deletionChanges) {
            const isDeletingFCDefault = change.productionCapability === 'Default_FC_PC' && change.handlingTier === 'DEFAULT';
            
            const request: DeleteR2FThresholdsRequest = {
                warehouseId: effectiveWarehouseId,
                productionCapability: change.productionCapability,
                warehouseHandlingTier: change.handlingTier !== 'DEFAULT' ? change.handlingTier : undefined,
                userAlias: change.userAlias || userAlias,
                actionSource: change.actionSource || 'Newton Dashboard',
                reason: change.reason || 'UI Delete'
            };
            
            try {
                await deleteMutation.mutateAsync(request);
                
                // If we're deleting the FC default PC, force a refresh to ensure Network default is shown
                if (isDeletingFCDefault) {
                    queryClient.invalidateQueries(['r2f-thresholds', effectiveWarehouseId], { 
                        refetchActive: true,
                        refetchInactive: true 
                    });
                }
            } catch (error) {
                console.error('Failed to process deletion:', error);
                // Continue with other changes even if one deletion fails
            }
        }
        
        // Only process non-deletion changes if there are any
        if (nonDeletionChanges.length > 0) {
            await saveMutation.mutateAsync(nonDeletionChanges);
        }
    };

    // Calculate available handling tiers
    const availableTiers = useMemo(() => {
        if (!adaptedData) return Object.values(WarehouseHandlingTier) as string[];
        
        // Get all handling tier enum values
        const allTiers = Object.values(WarehouseHandlingTier) as string[];
        
        // Get currently used production capabilities
        const usedTiers = new Set(
            adaptedData.productionCapabilities.map(pc => pc.name)
        );
        
        // Return tiers that are not already in use
        return allTiers.filter(tier => !usedTiers.has(tier as string));
    }, [adaptedData]);

    // Function to add a new production capability from handling tier
    const addProductionCapability = async (handlingTier: string) => {
        try {
            // Create default nodes with standard threshold values
            const changes: PendingThresholdChange[] = [{
                productionCapability: handlingTier,
                handlingTier: 'DEFAULT',
                lowThreshold: 0,
                highThreshold: 100,
                userAlias: userAlias,
                reason: 'Create new production capability',
                action: 'Add',
                isDefault: true
            }];
            
            // Save the changes
            await saveThresholds(changes);
            
            return true;
        } catch (err) {
            return false;
        }
    };

    // Function to add a new production capability with custom name
    const addCustomPC = async (pcName: string) => {
        try {
            // Add a custom production capability
            
            // Create default nodes with standard threshold values
            const changes: PendingThresholdChange[] = [{
                productionCapability: pcName,
                handlingTier: 'DEFAULT',
                lowThreshold: 0,
                highThreshold: 100,
                userAlias: userAlias,
                reason: 'Create custom production capability',
                action: 'Add',
                isDefault: true
            }];
            
            // Save the changes
            await saveThresholds(changes);
            
            // Successfully added the production capability
            
            // Force full refresh of data after adding a PC
            // This ensures we have clean state especially for Default_FC_PC
            await queryClient.invalidateQueries(['r2f-thresholds', effectiveWarehouseId], {
                refetchActive: true,
                inactive: false
            });
            
            return true;
        } catch (err) {
            console.error('Failed to add custom PC:', err);
            return false;
        }
    };

    // Mutation for deleting thresholds
    const deleteMutation = useMutation<void, Error, DeleteR2FThresholdsRequest>(
        async (request: DeleteR2FThresholdsRequest) => {
            // Call API
            const result = await deleteR2FThresholds(request);
            if (!result) {
                throw new Error('Failed to delete R2F threshold');
            }
            return;
        },
        {
            onSuccess: () => {
                // Invalidate queries to refresh data
                queryClient.invalidateQueries(['r2f-thresholds', effectiveWarehouseId]);
            }
        }
    );

    // Delete individual handling tier (node)
    const deleteHandlingTier = async (
        productionCapability: string,
        warehouseHandlingTier: string, 
        reason: string,
        actionSource: string = 'Newton Dashboard'
    ) => {
        try {
            const request: DeleteR2FThresholdsRequest = {
                warehouseId: effectiveWarehouseId,
                productionCapability,
                warehouseHandlingTier,
                userAlias,
                actionSource,
                reason
            };
            
            await deleteMutation.mutateAsync(request);
            return true;
        } catch (err) {
            return false;
        }
    };

    // Delete entire production capability
    const deleteProductionCapability = async (
        productionCapability: string, 
        reason: string,
        actionSource: string = 'Newton Dashboard'
    ) => {
        try {
            const request: DeleteR2FThresholdsRequest = {
                warehouseId: effectiveWarehouseId,
                productionCapability,
                userAlias,
                actionSource,
                reason
            };
            
            await deleteMutation.mutateAsync(request);
            return true;
        } catch (err) {
            return false;
        }
    };

    return {
        data: adaptedData,
        isLoading,
        error: error ? (error as Error).message : undefined,
        saveThresholds,
        userCanEdit,
        availableTiers,
        addProductionCapability,  // Renamed from addShipOption
        addCustomPC,
        deleteHandlingTier,
        deleteProductionCapability
    };
}

/**
 * Adapts the API response to the component data format
 */
function adaptApiResponse(response: { warehouseId: string; r2fThreshold: HandlingTierByPC }, warehouseId: string): R2FThresholdData {
    const productionCapabilities: R2FProductionCapabilityData[] = [];
    
    // Transform the API response structure to the component structure
    Object.entries(response.r2fThreshold || {}).forEach(([pcName, nodeMap]) => {
        const pcId = `pc_${pcName.toLowerCase().replace(/\s+/g, '_')}`;
        const nodes: R2FNodeData[] = [];
        let defaultNode: R2FNodeData | null = null;
        
        // Determine if this is a network default or FC default
        const isNetworkDefault = pcName === 'Default_Network_PC';
        const isFCDefault = pcName === 'Default_FC_PC';
        const defaultType = isNetworkDefault ? 'Network' : (isFCDefault ? 'FC' : undefined);
        
        // First check if there is a DEFAULT node in the data
        const defaultNodeExists = Object.keys(nodeMap).some(nodeId => nodeId.toUpperCase() === 'DEFAULT');
        
        // Process each node in this production capability
        Object.entries(nodeMap).forEach(([nodeId, threshold]) => {
            const isDefault = nodeId.toUpperCase() === 'DEFAULT';
            const node: R2FNodeData = {
                id: `${pcId}_${nodeId.toLowerCase().replace(/\s+/g, '_')}`,
                handlingTier: nodeId,
                lowThreshold: threshold.lowThreshold,
                highThreshold: threshold.highThreshold,
                publishDateEpochMillis: threshold.publishDateEpochMillis,
                userAlias: threshold.userAlias,
                actionSource: threshold.actionSource,
                reason: threshold.reason,
                isDefault,
                defaultType: isDefault ? defaultType : undefined
            };
            
            if (isDefault) {
                defaultNode = node;
                // Skip adding default node to additionalNodes
            } else {
                nodes.push(node);
            }
        });
        
        // Do not create a synthetic default node if none exists
        // This allows PCs to exist without a DEFAULT tier
        
        // Add this production capability to the list
        productionCapabilities.push({
            id: pcId,
            name: pcName,
            defaultNode: defaultNode || undefined, // Only include if it exists
            additionalNodes: nodes,
            isNetworkDefault,
            isFCDefault,
            hasDefaultNode: !!defaultNode // Explicitly track whether it has a default node
        });
    });
    
    return {
        warehouseId,
        productionCapabilities
    };
}

/**
 * Prepares the threshold publish request from the pending changes,
 * returning only the non-deletion changes for the publish API
 */
function prepareThresholdPublishRequest(
    changes: PendingThresholdChange[],
    warehouseId: string,
    userAlias: string
): R2FToPublish[] {
    // Filter out deletion changes (those will be handled separately)
    const nonDeletionChanges = changes.filter(change => change.action !== 'Delete');
    
    // Group changes by production capability
    const changesByPC: Record<string, PendingThresholdChange[]> = {};
    
    // Process non-deletion changes
    nonDeletionChanges.forEach(change => {
        const pc = change.productionCapability;
        if (!changesByPC[pc]) {
            changesByPC[pc] = [];
        }
        changesByPC[pc].push(change);
    });
    
    // Create the publish request
    const thresholdsToPublish: HandlingTierByPC = {};
    
    // Process each production capability
    Object.entries(changesByPC).forEach(([pc, pcChanges]) => {
        if (!thresholdsToPublish[pc]) {
            thresholdsToPublish[pc] = {};
        }
        
        // Process each change for this production capability
        pcChanges.forEach(change => {
            // Add or modify
            const threshold: PublishedR2FThresholds = {
                lowThreshold: change.lowThreshold,
                highThreshold: change.highThreshold,
                userAlias: change.userAlias || userAlias,
                reason: change.reason || 'UI Update',
                publishDateEpochMillis: Date.now(),
                actionSource: change.actionSource || 'Newton Dashboard'
            };
            
            thresholdsToPublish[pc][change.handlingTier] = threshold;
        });
    });
    
    // Create the final request for non-deletion changes
    return [{
        warehouseId,
        r2fToPublish: thresholdsToPublish
    }];
}
